import React from 'react';
import ReactDOM from 'react-dom';
import { Calendar } from 'lucide-react';
import { todayISO } from '../utils/clinicDate';

// Multi-select capable date picker — shows a calendar with available dates highlighted.
// availableDates: Set of 'YYYY-MM-DD' strings that have records.
// selectedDates: Set of selected 'YYYY-MM-DD' strings.
// onChange(newSet) called with the new Set on toggle.
// ── BillingDatePicker — calendar popup with available dates highlighted ───────
export default function DatePicker({ selectedDate, selectedDates, onChange, onMultiChange, availableDates, multiSelect = false, showAllDates = true }) {
  const [open, setOpen] = React.useState(false);
  const [viewYear, setViewYear]   = React.useState(() => {
    const d = selectedDate && selectedDate !== 'all' ? new Date(selectedDate) : new Date();
    return d.getFullYear();
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    const d = selectedDate && selectedDate !== 'all' ? new Date(selectedDate) : new Date();
    return d.getMonth();
  });
  const btnRef  = React.useRef(null);
  const [pos, setPos] = React.useState({ top:0, left:0 });

  const todayStr = todayISO();
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAY_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function open_() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    let left = r.left;
    if (left + 290 > window.innerWidth - 8) left = window.innerWidth - 298;
    setPos({ top: r.bottom + 6, left: Math.max(left, 8) });
    setOpen(true);
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); }
    else setViewMonth(m => m-1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); }
    else setViewMonth(m => m+1);
  }

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const cells = [];
  for (let i=0; i<firstDay; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);

  const multiLabel = multiSelect && selectedDates && selectedDates.size > 0
    ? selectedDates.size === 1
      ? [...selectedDates][0]
      : `${selectedDates.size} dates selected`
    : null;
  const label = multiLabel
    || (selectedDate && selectedDate !== 'all'
      ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
      : selectedDate === 'all'
        ? 'All Dates'
        : 'Today — ' + new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short' }));

  // Whether "all dates" is genuinely the active filter — in multi-select
  // mode this must be based on selectedDates being empty, not on
  // selectedDate==='all' alone, since selecting 2+ specific dates can leave
  // selectedDate as 'all' (there's no single date to represent it) even
  // though the filter is very much NOT "everything".
  const isAllSelected = multiSelect
    ? !(selectedDates && selectedDates.size > 0)
    : selectedDate === 'all';

  function clearToAllDates() {
    onChange('all');
    if (multiSelect && onMultiChange) onMultiChange(new Set());
  }

  return (
    <>
      <button ref={btnRef} type="button" onClick={open_}
        className="btn btn-outline btn-sm"
        style={{ display:'flex', alignItems:'center', gap:7, fontWeight:600, minWidth:148 }}>
        <Calendar size={14} />
        {label}
        <span style={{ marginLeft:'auto', opacity:0.5, fontSize:10 }}>▼</span>
      </button>

      {!isAllSelected && (
        <button className="btn btn-ghost btn-sm" onClick={clearToAllDates}
          style={{ padding:'4px 8px', fontSize:12 }} title="Show all dates">
          × All
        </button>
      )}

      {open && ReactDOM.createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:3998 }} />
          <div style={{
            position:'fixed', top:pos.top, left:pos.left, zIndex:3999,
            background:'var(--color-surface)',
            border:'2px solid var(--color-border)',
            borderRadius:12,
            boxShadow:'var(--shadow-lg)',
            width:290, overflow:'hidden',
          }}>
            {/* "All dates" option at top */}
            {showAllDates && <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--color-border)' }}>
              <button type="button"
                onClick={() => { clearToAllDates(); setOpen(false); }}
                style={{
                  width:'100%', padding:'7px 12px', borderRadius:8, cursor:'pointer', textAlign:'left',
                  fontWeight:700, fontSize:13,
                  background: isAllSelected ? 'var(--color-primary)' : 'var(--color-surface-raised)',
                  color: isAllSelected ? '#fff' : 'var(--color-ink)',
                  border: `1px solid ${isAllSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                }}>
                All dates
                {availableDates.size > 0 && (
                  <span style={{ fontSize:11, fontWeight:400, marginLeft:8, opacity:0.7 }}>
                    {availableDates.size} date{availableDates.size!==1?'s':''} with records
                  </span>
                )}
              </button>
              {multiSelect && selectedDates && selectedDates.size > 0 && (
                <div style={{ marginTop:6, fontSize:11, color:'var(--color-ink-faint)' }}>
                  {selectedDates.size} date{selectedDates.size!==1?'s':''} selected — click a date below to add or remove it
                </div>
              )}
            </div>}

            {/* Month navigation */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px 6px' }}>
              <button type="button" onClick={prevMonth} className="btn btn-ghost btn-sm" style={{ padding:'4px 8px' }}>‹</button>
              <span style={{ fontWeight:700, fontSize:13 }}>{MONTHS[viewMonth]} {viewYear}</span>
              <button type="button" onClick={nextMonth} className="btn btn-ghost btn-sm" style={{ padding:'4px 8px' }}>›</button>
            </div>

            {/* Day labels */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'0 10px', gap:2, marginBottom:4 }}>
              {DAY_LABELS.map(d => (
                <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'var(--color-ink-faint)', padding:'2px 0' }}>{d}</div>
              ))}
            </div>

            {/* Date cells */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'0 10px 12px', gap:2 }}>
              {cells.map((day, i) => {
                if (!day) return <div key={i} />;
                const mm = String(viewMonth+1).padStart(2,'0');
                const dd = String(day).padStart(2,'0');
                const dateStr = `${viewYear}-${mm}-${dd}`;
                const hasRecords = availableDates.size === 0 || availableDates.has(dateStr);
                const isSelected = multiSelect ? (selectedDates||new Set()).has(dateStr) : selectedDate === dateStr;
                const isToday    = dateStr === todayStr;
                return (
                  <button key={i} type="button"
                    disabled={availableDates.size > 0 && !hasRecords}
                    onClick={() => {
                        if (multiSelect && onMultiChange) {
                          const newSet = new Set(selectedDates || []);
                          if (newSet.has(dateStr)) newSet.delete(dateStr);
                          else newSet.add(dateStr);
                          onMultiChange(newSet);
                        } else {
                          onChange(dateStr); setOpen(false);
                        }
                      }}
                    style={{
                      aspectRatio:'1', borderRadius:6, border:'none', cursor: hasRecords ? 'pointer' : 'not-allowed',
                      fontSize:12, fontWeight: hasRecords ? 700 : 400,
                      background: isSelected ? 'var(--color-primary)'
                               : isToday && hasRecords ? 'var(--color-primary-soft)'
                               : 'transparent',
                      color: isSelected ? '#fff'
                           : hasRecords ? (isToday ? 'var(--color-primary)' : 'var(--color-ink)')
                           : 'var(--color-ink-faint)',
                      opacity: hasRecords ? 1 : 0.35,
                      outline: isToday && !isSelected ? '1px solid var(--color-primary)' : 'none',
                    }}>
                    {day}
                    {hasRecords && !isSelected && (
                      <div style={{ width:4, height:4, borderRadius:'50%', background:'var(--color-primary)', margin:'1px auto 0' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}